import { useMutation, useQueryClient } from '@tanstack/react-query';
import TaskService from '@/services/TaskService';
import type {
  CreateTaskPayload, EscalateOpts, TaskInstance, CompletionActor,
} from '@/services/TaskService';
import { TASK_KEYS } from './keys';
import { PATIENT_HOME_KEYS } from '@/state/data/patientHome/keys';
import {
  patchArrayCaches, rollbackSnapshot, scheduleInvalidate, WF_RECONCILE_DELAY_MS,
} from '@/state/data/_shared/wf-race';

// Async writes via Tier 4 → n8n workflow. See _shared/wf-race.ts.

// A task write also changes the patient's home view (Patient Home tab section +
// companion preview) — reconcile that patient's cache alongside the task caches.
// The patient-file surfaces poll until the write lands (pollTasksUntil); the Patient Home
// section has only a 30s optimistic overlay, so ONE reconcile at 3s renders any slower WF17
// write as if it never happened. Re-read across that whole window — the last pass lands just
// inside the TTL, and every action on that section shares the overlay, so this belongs here
// rather than on the two snooze mutations alone.
const HOME_RECONCILE_PASSES_MS = [WF_RECONCILE_DELAY_MS, 9000, 20000, 29000];
function invalidateTaskViews(qc: ReturnType<typeof useQueryClient>, withingsUserId: string | number) {
  scheduleInvalidate(qc, TASK_KEYS.all);
  HOME_RECONCILE_PASSES_MS.forEach((ms) => scheduleInvalidate(qc, PATIENT_HOME_KEYS.byUser(withingsUserId), ms));
}

export function useSnoozeTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: {
      taskId: string | number; until: string; reason: string;
      withingsUserId: string | number; actor?: CompletionActor;
    }) => (
      TaskService.snoozeTask(v.taskId, {
        until: v.until, reason: v.reason, withingsUserId: v.withingsUserId, actor: v.actor,
      })
    ),
    // Stamp the snooze locally so the pill appears immediately — the write lands via
    // Tier 4 → WF5 → WF17, which the delayed invalidates below reconcile.
    // Mirrors exactly what WF17 merges into context_json.snooze.
    onMutate: ({
      taskId, until, reason, actor,
    }) => {
      const by = actor
        ? {
          user_id: actor.user_id ?? null,
          firstname: actor.firstname ?? null,
          lastname: actor.lastname ?? null,
        }
        : null;
      const previous = patchArrayCaches<TaskInstance>(qc, TASK_KEYS.all, (t) => (
        String(t.task_id) === String(taskId)
          ? {
            ...t,
            context_json: {
              ...((t.context_json as Record<string, unknown>) ?? {}),
              snooze: {
                until, reason, by, at: new Date().toISOString(),
              },
            },
          }
          : t
      ));
      return { previous };
    },
    onError: (_e, _v, ctx) => rollbackSnapshot(qc, ctx?.previous),
    onSettled: (_d, _e, v) => invalidateTaskViews(qc, v.withingsUserId),
  });
}

export function useResumeTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { taskId: string | number; withingsUserId: string | number; actor?: CompletionActor }) => (
      TaskService.resumeTask(v.taskId, { withingsUserId: v.withingsUserId, actor: v.actor })
    ),
    // Drop the pill immediately; the delayed invalidate confirms it against the row WF17 wrote.
    onMutate: ({ taskId }) => {
      const previous = patchArrayCaches<TaskInstance>(qc, TASK_KEYS.all, (t) => {
        if (String(t.task_id) !== String(taskId)) return t;
        const ctx = { ...((t.context_json as Record<string, unknown>) ?? {}) };
        delete ctx.snooze;
        return { ...t, context_json: ctx };
      });
      return { previous };
    },
    onError: (_e, _v, ctx) => rollbackSnapshot(qc, ctx?.previous),
    onSettled: (_d, _e, v) => invalidateTaskViews(qc, v.withingsUserId),
  });
}

export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateTaskPayload) => TaskService.createTask(payload),
    // New rows have a server-assigned id, so we can't optimistically add.
    onSettled: () => scheduleInvalidate(qc, TASK_KEYS.all),
  });
}

export function useCompleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      taskId: string | number; notes?: string; withingsUserId: string | number;
      actor?: CompletionActor; extraChanges?: Record<string, unknown>;
    }) => (
      TaskService.completeTask(vars.taskId, vars.withingsUserId, vars.notes ?? '', vars.actor, vars.extraChanges)
    ),
    onMutate: ({ taskId, notes, actor }) => {
      // Optimistically stamp the completer so the card shows the name before the
      // WF17 cascade lands (mirrors what WF17 writes to context_json.completed_by).
      const completedBy = actor && (actor.firstname || actor.lastname)
        ? { user_id: actor.user_id ?? null, firstname: actor.firstname ?? null, lastname: actor.lastname ?? null }
        : null;
      const previous = patchArrayCaches<TaskInstance>(qc, TASK_KEYS.all, (t) => (
        String(t.task_id) === String(taskId)
          ? {
            ...t,
            status: 'completed',
            completion_source: t.completion_source ?? 'staff',
            completion_notes: notes ?? t.completion_notes,
            ...(completedBy
              ? { context_json: { ...((t.context_json as Record<string, unknown>) ?? {}), completed_by: completedBy } }
              : {}),
          }
          : t
      ));
      return { previous };
    },
    onError: (_e, _v, ctx) => rollbackSnapshot(qc, ctx?.previous),
    onSettled: (_d, _e, vars) => invalidateTaskViews(qc, vars.withingsUserId),
  });
}

// Completes "Review baseline labs" with the care team's note, then reads it back (no optimistic row: the
// result says whether the completion and the note landed).
export function useCompleteLabReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      taskId: string | number; withingsUserId: string | number; labNote: string; notes?: string; actor?: CompletionActor;
    }) => TaskService.completeLabReview(vars.taskId, vars.withingsUserId, { labNote: vars.labNote, notes: vars.notes, actor: vars.actor }),
    // The read-back already saw Done, so there is no WF17 race to wait out for the task lists.
    onSuccess: (r) => { if (r.completed) qc.invalidateQueries({ queryKey: TASK_KEYS.all }); },
    onSettled: (_d, _e, vars) => invalidateTaskViews(qc, vars.withingsUserId),
  });
}

export function useCancelTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { taskId: string | number; notes?: string; withingsUserId: string | number; actor?: CompletionActor }) => (
      TaskService.cancelTask(vars.taskId, vars.withingsUserId, vars.notes ?? '', vars.actor)
    ),
    onMutate: ({ taskId, notes }) => {
      const previous = patchArrayCaches<TaskInstance>(qc, TASK_KEYS.all, (t) => (
        String(t.task_id) === String(taskId)
          ? {
            ...t,
            status: 'cancelled',
            completion_notes: notes ?? t.completion_notes,
            modified: new Date().toISOString(),
          }
          : t
      ));
      return { previous };
    },
    onError: (_e, _v, ctx) => rollbackSnapshot(qc, ctx?.previous),
    onSettled: (_d, _e, vars) => invalidateTaskViews(qc, vars.withingsUserId),
  });
}

export function useReopenTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      taskId: string | number;
      withingsUserId: string | number;
      clearNotes?: boolean;
      toStatus?: 'pending' | 'scheduled';
      actor?: CompletionActor;
    }) => (
      TaskService.reopenTask(vars.taskId, vars.withingsUserId, {
        clearNotes: vars.clearNotes, toStatus: vars.toStatus, actor: vars.actor,
      })
    ),
    onMutate: ({ taskId, clearNotes, toStatus }) => {
      // Optimistically reopen + clear the completion stamp (mirrors WF17's reopen path).
      const previous = patchArrayCaches<TaskInstance>(qc, TASK_KEYS.all, (t) => (
        String(t.task_id) === String(taskId)
          ? {
            ...t,
            status: toStatus ?? 'pending',
            completion_source: null,
            completed_at: null,
            ...(clearNotes ? { completion_notes: null } : {}),
          }
          : t
      ));
      return { previous };
    },
    onError: (_e, _v, ctx) => rollbackSnapshot(qc, ctx?.previous),
    onSettled: (_d, _e, vars) => invalidateTaskViews(qc, vars.withingsUserId),
  });
}

export function useUpdateTaskRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { taskId: string | number; role: string; withingsUserId: string | number }) => (
      TaskService.updateTaskRole(vars.taskId, vars.role, vars.withingsUserId)
    ),
    onMutate: ({ taskId, role }) => {
      const previous = patchArrayCaches<TaskInstance>(qc, TASK_KEYS.all, (t) => (
        String(t.task_id) === String(taskId) ? { ...t, owner_role: role } : t
      ));
      return { previous };
    },
    onError: (_e, _v, ctx) => rollbackSnapshot(qc, ctx?.previous),
    onSettled: () => scheduleInvalidate(qc, TASK_KEYS.all),
  });
}

export function useUpdateTaskPriority() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { taskId: string | number; priority: string; withingsUserId: string | number }) => (
      TaskService.updateTaskPriority(vars.taskId, vars.priority, vars.withingsUserId)
    ),
    onMutate: ({ taskId, priority }) => {
      const previous = patchArrayCaches<TaskInstance>(qc, TASK_KEYS.all, (t) => (
        String(t.task_id) === String(taskId) ? { ...t, priority } : t
      ));
      return { previous };
    },
    onError: (_e, _v, ctx) => rollbackSnapshot(qc, ctx?.previous),
    onSettled: () => scheduleInvalidate(qc, TASK_KEYS.all),
  });
}

export function useUpdateTaskNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { taskId: string | number; notes: string; withingsUserId: string | number }) => (
      TaskService.updateTaskNote(vars.taskId, vars.notes, vars.withingsUserId)
    ),
    onMutate: ({ taskId, notes }) => {
      const previous = patchArrayCaches<TaskInstance>(qc, TASK_KEYS.all, (t) => (
        String(t.task_id) === String(taskId) ? { ...t, completion_notes: notes } : t
      ));
      return { previous };
    },
    onError: (_e, _v, ctx) => rollbackSnapshot(qc, ctx?.previous),
    onSettled: () => scheduleInvalidate(qc, TASK_KEYS.all),
  });
}

export function useEscalateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { taskId: string | number } & EscalateOpts) => (
      TaskService.escalateToClinician(vars.taskId, { reason: vars.reason, priority: vars.priority, withingsUserId: vars.withingsUserId })
    ),
    onMutate: ({ taskId, priority, reason }) => {
      const stamp = `[Escalated by Care Coordinator @ ${new Date().toISOString()}] ${reason ?? ''}`.trim();
      const previous = patchArrayCaches<TaskInstance>(qc, TASK_KEYS.all, (t) => {
        if (String(t.task_id) !== String(taskId)) return t;
        const prevNotes = t.completion_notes ? `\n${t.completion_notes}` : '';
        return {
          ...t,
          owner_role: 'clinician',
          priority: priority ?? 'urgent',
          completion_notes: stamp + prevNotes,
        };
      });
      return { previous };
    },
    onError: (_e, _v, ctx) => rollbackSnapshot(qc, ctx?.previous),
    onSettled: () => scheduleInvalidate(qc, TASK_KEYS.all),
  });
}
